import {BaseApi} from './BaseApi'
import {sanitizePayload} from '../utils/apiUtils'
import {reportCatchError} from '../utils/errorReporter'

// WC Rate Type - removed enum, using string type instead

// WC Code Interfaces
export interface WCCode {
  _id: string
  id: string
  state: string
  wcCode: string
  description: string
  country?: string // Updated field name to match backend
  rateType: string
  manualRate: number
  multiplier: number
  effectiveRate: number // Calculated field (manualRate * multiplier)
  createdDate?: Date
  modifiedDate?: Date
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
}

export interface CreateWCCodeRequest {
  state: string
  wcCode: string
  description: string
  country?: string // Updated field name to match backend
  rateType: string
  manualRate: number
  multiplier: number
}

export interface UpdateWCCodeRequest {
  id: string
  state: string
  wcCode: string
  description: string
  country?: string // Updated field name to match backend
  rateType: string
  manualRate: number
  multiplier: number
  versionKey?: number
}

export interface WCCodeNameIdModel {
  id: string
  wcCode: string
  state: string
  description: string
}

export interface WCCodePagingModel {
  data: WCCode[]
  totalCount: number
  currentPage: number
  pageSize: number
  totalPages: number
}

export interface WCCodeImportError {
  row: number
  field: string
  value: any
  error: string
}

export interface WCCodeImportResult {
  totalRows: number
  successfulImports: number
  failedImports: number
  errors: WCCodeImportError[]
  createdWCCodeIds: string[]
}

export interface WCCodeImportCatalogResult {
  successCount: number
  failureCount: number
  errors: WCCodeImportError[]
  createdWCCodeIds: string[]
}

// Paging Parameters Interface
export interface PagingParametersDto {
  pageNumber?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

// WC Codes with Availability Response Interface
export interface WCCodeWithAvailability {
  id: string
  state: string
  wcCode: string
  description: string
  country?: string
  rateType: string
  manualRate: number
  multiplier: number
  effectiveRate: number
  isAvailable: boolean // Indicates if this WC code can be assigned to catalogs
  createdDate?: Date
  modifiedDate?: Date
  createdBy?: string
  modifiedBy?: string
  versionKey?: number
}

export interface WCCodesWithAvailabilityResponse {
  wcCodes: WCCodeWithAvailability[]
  availableStates: string[] // List of states that have available WC codes
}

// Search filters interface for backward compatibility
export interface SearchWCCodeFilters {
  state?: string
  searchTerm?: string
  wcCode?: string
  description?: string
  country?: string
}

/**
 * WC Code API Service
 * Handles all workers' compensation code related operations
 */
export class WCCodeApi extends BaseApi {
  /**
   * Get paginated list of WC Codes
   */
  async getWCCodesPaginated(pagingParams?: PagingParametersDto): Promise<WCCodePagingModel> {
    try {
      const params = new URLSearchParams()
      if (pagingParams?.pageNumber) params.append('pageNumber', pagingParams.pageNumber.toString())
      if (pagingParams?.pageSize) params.append('pageSize', pagingParams.pageSize.toString())
      if (pagingParams?.sortBy) params.append('sortBy', pagingParams.sortBy)
      if (pagingParams?.sortDirection) params.append('sortDirection', pagingParams.sortDirection)

      const response = await this.client.get<WCCodePagingModel>(
        `/wc-code/getWCCodes?${params.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC codes:', error)
      throw error
    }
  }

  /**
   * Get WC codes with optional filtering (backward compatibility)
   * This method provides the interface that existing components expect
   */
  async getWCCodes(filters?: SearchWCCodeFilters): Promise<WCCode[]> {
    try {
      // If filters provided, use search endpoint, otherwise get all
      if (
        filters &&
        (filters.state || filters.searchTerm || filters.wcCode || filters.description)
      ) {
        // Convert old filter format to new backend format
        const state = filters.state
        const searchTerms = [filters.searchTerm, filters.wcCode, filters.description].filter(
          Boolean,
        )

        const searchTerm = searchTerms.length > 0 ? searchTerms.join(' ') : undefined

        return await this.searchWCCodes(state, searchTerm)
      } else {
        return await this.getAllWCCodes()
      }
    } catch (error: unknown) {
      reportCatchError('Error fetching WC codes with filters:', error)
      throw error
    }
  }

  /**
   * Get all WC Codes (non-paginated)
   */
  async getAllWCCodes(): Promise<WCCode[]> {
    try {
      const response = await this.client.get<WCCode[]>('/wc-code/getAllWCCodes')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching all WC codes:', error)
      throw error
    }
  }

  /**
   * Get WC Codes with availability status for catalog assignment
   * Returns available states and WC codes that can be assigned to catalogs
   */
  async getWcCodesWithAvailability(): Promise<WCCodeWithAvailability[]> {
    try {
      const response = await this.client.get<WCCodeWithAvailability[]>(
        '/wc-code/getWcCodesWithAvailability',
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC codes with availability:', error)
      throw error
    }
  }

  /**
   * Get WC Codes by state
   */
  async getWCCodesByState(state: string): Promise<WCCode[]> {
    try {
      const response = await this.client.get<WCCode[]>(`/wc-code/getWCCodesByState/${state}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC codes by state:', error)
      throw error
    }
  }

  /**
   * Get WC Code by ID
   */
  async getWCCodeById(id: string): Promise<WCCode> {
    try {
      const response = await this.client.get<WCCode>(`/wc-code/getWCCode/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC code by ID:', error)
      throw error
    }
  }

  /**
   * Search WC Codes by state and/or search term
   */
  async searchWCCodes(state?: string, searchTerm?: string): Promise<WCCode[]> {
    try {
      const params = new URLSearchParams()
      if (state) params.append('state', state)
      if (searchTerm) params.append('searchTerm', searchTerm)

      const response = await this.client.get<WCCode[]>(
        `/wc-code/searchWCCodes?${params.toString()}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error searching WC codes:', error)
      throw error
    }
  }

  /**
   * Get WC Code names by IDs (batch lookup)
   */
  async getWCCodeNamesByIds(wcCodeIds: string[]): Promise<WCCodeNameIdModel[]> {
    try {
      const response = await this.client.post<WCCodeNameIdModel[]>(
        '/wc-code/getWCCodeNamesByIds',
        wcCodeIds,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching WC code names by IDs:', error)
      throw error
    }
  }

  /**
   * Create a new WC Code (Platform Admin only)
   */
  async createWCCode(wcCode: CreateWCCodeRequest): Promise<WCCode> {
    try {
      const sanitizedData = sanitizePayload(wcCode as unknown as Record<string, unknown>)
      const response = await this.client.post<WCCode>('/wc-code/createWCCode', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating WC code:', error)
      throw error
    }
  }

  /**
   * Update an existing WC Code (Platform Admin only)
   */
  async updateWCCode(wcCode: UpdateWCCodeRequest): Promise<WCCode>
  async updateWCCode(id: string, data: Partial<CreateWCCodeRequest>): Promise<WCCode>
  async updateWCCode(
    wcCodeOrId: UpdateWCCodeRequest | string,
    data?: Partial<CreateWCCodeRequest>,
  ): Promise<WCCode> {
    try {
      let updateData: UpdateWCCodeRequest

      if (typeof wcCodeOrId === 'string') {
        // Backward compatibility: (id, data) signature
        updateData = {
          id: wcCodeOrId,
          ...(data as Partial<CreateWCCodeRequest>),
        } as UpdateWCCodeRequest
      } else {
        // New signature: (wcCode) signature
        updateData = wcCodeOrId
      }

      const sanitizedData = sanitizePayload(updateData as unknown as Record<string, unknown>)
      const response = await this.client.put<WCCode>('/wc-code/updateWCCode', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating WC code:', error)
      throw error
    }
  }

  /**
   * Delete a WC Code (Platform Admin only)
   */
  async deleteWCCode(id: string): Promise<WCCode> {
    try {
      const response = await this.client.delete<WCCode>(`/wc-code/deleteWCCode/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error deleting WC code:', error)
      throw error
    }
  }

  /**
   * Import WC Codes from CSV/Excel file (Platform Admin only)
   */
  async importWCCodes(file: File): Promise<WCCodeImportResult>
  async importWCCodes(formData: FormData): Promise<WCCodeImportResult>
  async importWCCodes(fileOrFormData: File | FormData): Promise<WCCodeImportResult> {
    try {
      let formData: FormData

      if (fileOrFormData instanceof File) {
        // If a File is passed, create FormData
        formData = new FormData()
        formData.append('file', fileOrFormData)
      } else {
        // If FormData is passed directly, use it
        formData = fileOrFormData
      }

      const response = await this.client.post<WCCodeImportResult>(
        '/wc-code/importWCCodes',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error importing WC codes:', error)
      throw error
    }
  }
}

// Export singleton instance
export const wcCodeApi = new WCCodeApi()
export default wcCodeApi
